/**
 * `/customers/codex-capacity-alerts` (#2575) — the operator surface for
 * the Codex at-capacity alert.
 *
 * An independent page, not a tab under balance alerts: the two channels
 * mail different facts and share nothing but "this press reaches a
 * paying customer".
 *
 * Layout is deliberate. The SWITCH and the live latch sit above the
 * tabs, always on screen. The switch ships closed, so "off" is the
 * ordinary state of a freshly promoted environment and the whole point
 * of the default evaporates if that state is quiet. The latch answers
 * "what happens next" in a sentence, not a dump of field names.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useT } from '../i18n/index.ts';
import { CODEX_CAPACITY_ALERT_HISTORY_LIMIT } from '../api/codexCapacityAlerts.ts';
import {
  useCodexCapacityAlertHistory,
  useCodexCapacityAlertIncidents,
  useCodexCapacityAlertPreview,
  useCodexCapacityAlertSettings,
  useCodexCapacityAlertState,
  useCodexCapacityAlertSuppressions,
  useCodexCapacityAlertTestSend,
  useCodexCapacityExcludedSkus,
  useCreateCodexCapacityAlertSuppression,
  useCreateCodexCapacityExcludedSku,
  useDeleteCodexCapacityAlertSuppression,
  useDeleteCodexCapacityExcludedSku,
  useUpdateCodexCapacityAlertSettings,
} from '../api/useCodexCapacityAlerts.ts';
import { CodexCapacityExcludedSkusPanel } from '../components/codex-capacity/CodexCapacityExcludedSkusPanel.tsx';
import { CodexCapacityHistoryTable } from '../components/codex-capacity/CodexCapacityHistoryTable.tsx';
import { CodexCapacityIncidentsTable } from '../components/codex-capacity/CodexCapacityIncidentsTable.tsx';
import { CodexCapacityPreviewPanel } from '../components/codex-capacity/CodexCapacityPreviewPanel.tsx';
import { CodexCapacitySettingsForm } from '../components/codex-capacity/CodexCapacitySettingsForm.tsx';
import { CodexCapacityStatePanel } from '../components/codex-capacity/CodexCapacityStatePanel.tsx';
import { CodexCapacitySuppressionsPanel } from '../components/codex-capacity/CodexCapacitySuppressionsPanel.tsx';
import { CodexCapacitySwitchPanel } from '../components/codex-capacity/CodexCapacitySwitchPanel.tsx';
import { CodexCapacityTestSendPanel } from '../components/codex-capacity/CodexCapacityTestSendPanel.tsx';
import { useToast } from '../components/Toast.tsx';
import {
  Button,
  EmptyState,
  PageHeader,
  Panel,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/index.ts';

const TAB_SETTINGS = 'settings';
const TAB_PREVIEW = 'preview';
const TAB_INCIDENTS = 'incidents';
const TAB_HISTORY = 'history';
const TAB_SUPPRESSIONS = 'suppressions';
const TAB_EXCLUDED = 'excluded-skus';
const TAB_TEST_SEND = 'test-send';

export function CodexCapacityAlertsPage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [tab, setTab] = useState<string>(TAB_SETTINGS);
  const [historyIncidentId, setHistoryIncidentId] = useState<string | null>(null);

  const settings = useCodexCapacityAlertSettings();
  const state = useCodexCapacityAlertState();
  const preview = useCodexCapacityAlertPreview(tab === TAB_PREVIEW);
  const incidents = useCodexCapacityAlertIncidents();
  const history = useCodexCapacityAlertHistory(historyIncidentId);
  const suppressions = useCodexCapacityAlertSuppressions();
  const excluded = useCodexCapacityExcludedSkus();

  const updateSettings = useUpdateCodexCapacityAlertSettings();
  const createSuppression = useCreateCodexCapacityAlertSuppression();
  const deleteSuppression = useDeleteCodexCapacityAlertSuppression();
  const createExcluded = useCreateCodexCapacityExcludedSku();
  const deleteExcluded = useDeleteCodexCapacityExcludedSku();
  const testSend = useCodexCapacityAlertTestSend();

  const fail = (error: unknown): void => {
    toast.error(error instanceof Error ? error.message : t('codex_capacity.error.generic'));
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('codex_capacity.title')}
        sub={t('codex_capacity.subtitle')}
        testId="codex-capacity-header"
      />

      <CodexCapacitySwitchPanel
        settings={settings.data ?? null}
        loading={settings.isLoading}
        error={settings.isError}
        saving={updateSettings.isPending}
        onRetry={() => void settings.refetch()}
        onSubmit={(next) => {
          updateSettings.mutate(next, {
            onSuccess: (result) => {
              toast.success(
                t(result.enabled ? 'codex_capacity.toast.enabled' : 'codex_capacity.toast.disabled'),
              );
            },
            onError: fail,
          });
        }}
      />

      <CodexCapacityStatePanel
        settings={settings.data ?? null}
        state={state.data ?? null}
        loading={state.isLoading || settings.isLoading}
        error={state.isError || settings.isError}
        onRetry={() => {
          void settings.refetch();
          void state.refetch();
        }}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList ariaLabel={t('codex_capacity.tabs_label')}>
          <TabsTrigger value={TAB_SETTINGS} testId="codex-capacity-tab-settings">
            {t('codex_capacity.tab.settings')}
          </TabsTrigger>
          <TabsTrigger value={TAB_PREVIEW} testId="codex-capacity-tab-preview">
            {t('codex_capacity.tab.preview')}
          </TabsTrigger>
          <TabsTrigger value={TAB_INCIDENTS} testId="codex-capacity-tab-incidents">
            {t('codex_capacity.tab.incidents')}
          </TabsTrigger>
          <TabsTrigger value={TAB_HISTORY} testId="codex-capacity-tab-history">
            {t('codex_capacity.tab.history')}
          </TabsTrigger>
          <TabsTrigger value={TAB_SUPPRESSIONS} testId="codex-capacity-tab-suppressions">
            {t('codex_capacity.tab.suppressions')}
          </TabsTrigger>
          <TabsTrigger value={TAB_EXCLUDED} testId="codex-capacity-tab-excluded-skus">
            {t('codex_capacity.tab.excluded_skus')}
          </TabsTrigger>
          <TabsTrigger value={TAB_TEST_SEND} testId="codex-capacity-tab-test-send">
            {t('codex_capacity.tab.test_send')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={TAB_SETTINGS} className="mt-4">
          <Panel caption={t('codex_capacity.panel.settings')} padded testId="codex-capacity-settings-panel">
            <QueryBody
              loading={settings.isLoading}
              error={settings.isError}
              onRetry={() => void settings.refetch()}
              testId="codex-capacity-settings"
            >
              {settings.data === undefined ? null : (
                <CodexCapacitySettingsForm
                  settings={settings.data}
                  saving={updateSettings.isPending}
                  onSave={(body) => {
                    updateSettings.mutate(body, {
                      onSuccess: () => toast.success(t('codex_capacity.toast.settings_saved')),
                      onError: fail,
                    });
                  }}
                />
              )}
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_PREVIEW} className="mt-4">
          <Panel
            caption={t('codex_capacity.panel.preview')}
            count={preview.data?.recipients.length}
            testId="codex-capacity-preview-panel"
            padded
            actions={
              <Button
                size="sm"
                variant="ghost"
                loading={preview.isFetching}
                onClick={() => void preview.refetch()}
                data-testid="codex-capacity-preview-refresh"
              >
                {t('codex_capacity.preview.recompute')}
              </Button>
            }
          >
            <QueryBody
              loading={preview.isLoading}
              error={preview.isError}
              onRetry={() => void preview.refetch()}
              testId="codex-capacity-preview"
            >
              {preview.data === undefined ? null : <CodexCapacityPreviewPanel preview={preview.data} />}
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_INCIDENTS} className="mt-4">
          <Panel
            caption={t('codex_capacity.panel.incidents')}
            count={incidents.data?.length}
            testId="codex-capacity-incidents-panel"
            padded
          >
            <QueryBody
              loading={incidents.isLoading}
              error={incidents.isError}
              onRetry={() => void incidents.refetch()}
              testId="codex-capacity-incidents"
            >
              <CodexCapacityIncidentsTable
                incidents={incidents.data ?? []}
                onViewHistory={(incidentId) => {
                  setHistoryIncidentId(incidentId);
                  setTab(TAB_HISTORY);
                }}
              />
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_HISTORY} className="mt-4">
          <Panel
            caption={t('codex_capacity.panel.history')}
            count={history.data?.length}
            testId="codex-capacity-history-panel"
            padded
            actions={
              historyIncidentId === null ? null : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setHistoryIncidentId(null)}
                  data-testid="codex-capacity-history-filter-clear"
                >
                  {t('codex_capacity.history.filter_clear')}
                </Button>
              )
            }
          >
            <QueryBody
              loading={history.isLoading}
              error={history.isError}
              onRetry={() => void history.refetch()}
              testId="codex-capacity-history"
            >
              <CodexCapacityHistoryTable
                entries={history.data ?? []}
                limit={CODEX_CAPACITY_ALERT_HISTORY_LIMIT}
                incidentId={historyIncidentId}
              />
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_SUPPRESSIONS} className="mt-4">
          <Panel
            caption={t('codex_capacity.panel.suppressions')}
            count={suppressions.data?.length}
            testId="codex-capacity-suppressions-panel"
            padded
          >
            <QueryBody
              loading={suppressions.isLoading}
              error={suppressions.isError}
              onRetry={() => void suppressions.refetch()}
              testId="codex-capacity-suppressions"
            >
              <CodexCapacitySuppressionsPanel
                suppressions={suppressions.data ?? []}
                adding={createSuppression.isPending}
                removingAccountId={
                  deleteSuppression.isPending ? deleteSuppression.variables ?? null : null
                }
                onAdd={(accountId, reason) => {
                  createSuppression.mutate(
                    { accountId, reason },
                    {
                      onSuccess: () =>
                        toast.success(t('codex_capacity.toast.suppressed', { account: accountId })),
                      onError: fail,
                    },
                  );
                }}
                onRemove={(accountId) => {
                  deleteSuppression.mutate(accountId, {
                    onSuccess: (result) => {
                      toast.success(
                        t(
                          result.removed
                            ? 'codex_capacity.toast.restored'
                            : 'codex_capacity.toast.restore_noop',
                          { account: accountId },
                        ),
                      );
                    },
                    onError: fail,
                  });
                }}
              />
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_EXCLUDED} className="mt-4">
          <Panel
            caption={t('codex_capacity.panel.excluded_skus')}
            count={excluded.data?.length}
            testId="codex-capacity-excluded-panel"
            padded
          >
            <QueryBody
              loading={excluded.isLoading}
              error={excluded.isError}
              onRetry={() => void excluded.refetch()}
              testId="codex-capacity-excluded"
            >
              <CodexCapacityExcludedSkusPanel
                excluded={excluded.data ?? []}
                adding={createExcluded.isPending}
                removingSku={deleteExcluded.isPending ? deleteExcluded.variables ?? null : null}
                onAdd={(skuName) => {
                  createExcluded.mutate(skuName, {
                    onSuccess: () =>
                      toast.success(t('codex_capacity.toast.excluded_added', { sku: skuName })),
                    onError: fail,
                  });
                }}
                onRemove={(skuName) => {
                  deleteExcluded.mutate(skuName, {
                    onSuccess: (result) => {
                      toast.success(
                        t(
                          result.removed
                            ? 'codex_capacity.toast.excluded_removed'
                            : 'codex_capacity.toast.excluded_noop',
                          { sku: skuName },
                        ),
                      );
                    },
                    onError: fail,
                  });
                }}
              />
            </QueryBody>
          </Panel>
        </TabsContent>

        <TabsContent value={TAB_TEST_SEND} className="mt-4">
          <Panel caption={t('codex_capacity.panel.test_send')} padded testId="codex-capacity-test-send-panel">
            <CodexCapacityTestSendPanel
              sending={testSend.isPending}
              onSend={async (accountIds, kind) => {
                const result = await testSend.mutateAsync({ accountIds, kind });
                toast.success(
                  t('codex_capacity.toast.test_sent', {
                    sent: String(result.sent),
                    failed: String(result.delivery_failures),
                  }),
                );
              }}
            />
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function QueryBody({
  loading,
  error,
  onRetry,
  testId,
  children,
}: {
  readonly loading: boolean;
  readonly error: boolean;
  readonly onRetry: () => void;
  readonly testId: string;
  readonly children: ReactNode;
}): JSX.Element {
  const t = useT();
  if (loading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (error) {
    return (
      <EmptyState
        title={t('codex_capacity.error.load')}
        hint={t('codex_capacity.error.load_hint')}
        action={
          <Button variant="secondary" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        }
        testId={`${testId}-error`}
      />
    );
  }
  return <>{children}</>;
}
